import { useState } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  CalendarIcon,
  EnvelopeIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import useSubscriptionManagement from './useSubscriptionManagement';

export default function SubscriptionManagement() {
  const {
    subscriptions,
    loading,
    totalSubscriptions,
    currentPage,
    totalPages,
    filters,
    setFilters,
    setCurrentPage,
    clearFilters,
    viewDetail,
    planOptions,
  } = useSubscriptionManagement();

  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [selectedPlan, setSelectedPlan] = useState(filters.planId || '');

  const handleSearch = () => {
    setFilters({
      search: searchTerm,
      planId: selectedPlan,
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedPlan('');
    clearFilters();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Subscription Management</h1>
        <div className="text-sm text-gray-400">
          Total: <span className="text-primaryBlue font-semibold">{totalSubscriptions}</span>
        </div>
      </div>

      {/* Search and Filters Row */}
      <div className=" p-6 px-0 ">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* Search Bar - takes remaining width */}
          <div className="md:col-span-6 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-4 bg-blueGray border border-borderGray rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primaryBlue"
            />
          </div>

          {/* Plan Filter */}
          <div className="md:col-span-3">
            <select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              className="w-full px-4 py-4 bg-blueGray border border-borderGray rounded-lg text-white focus:outline-none focus:border-primaryBlue"
            >
              {planOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="md:col-span-3 flex gap-2">
            <button
              onClick={handleSearch}
              className="flex items-center gap-2 px-4 py-4 bg-blueGray hover:bg-blueGray/90 border border-borderGray text-white rounded-lg transition-colors w-full md:w-auto"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-2 px-4 py-4 bg-blueGray hover:bg-blueGray/70 border border-borderGray text-white rounded-lg transition-colors w-full md:w-auto"
            >
              <FunnelIcon className="h-4 w-4" />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className=" border-2 border-borderGray rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blueGray border-b border-borderGray">
              <tr>
                <th className="px-6 py-5 text-left text-base font-medium text-gray-300 uppercase tracking-wider">User</th>
                <th className="px-6 py-5 text-left text-base font-medium text-gray-300 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-5 text-left text-base font-medium text-gray-300 uppercase tracking-wider">Price</th>
                <th className="px-6 py-5 text-left text-base font-medium text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-5 text-left text-base font-medium text-gray-300 uppercase tracking-wider">Renewal Date</th>
                <th className="px-6 py-5 text-left text-base font-medium text-gray-300 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">Loading subscriptions...</td>
                </tr>
              ) : subscriptions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">No subscriptions found</td>
                </tr>
              ) : (
                subscriptions.map((sub) => {
                  const statusActive = sub.status === 'active' || sub.isActive;
                  return (
                    <tr key={sub.id} className="hover:bg-blueGray/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primaryBlue flex items-center justify-center">
                            <UserIcon className="h-6 w-6 text-white" />
                          </div>
                          <div className="ml-4">
                            <div className="text-base font-medium text-white">{sub.userName || sub.name || 'N/A'}</div>
                            <span className="text-sm text-gray-300">{sub.email || '-'}</span>
                            <div className="text-sm text-gray-300">ID: {sub.userId || sub.id}</div>
                          </div>
                        </div>
                      </td>
                     
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex  items-center px-2.5 py-0.5 rounded-full text-base font-medium  text-white">
                          {sub.planName  || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {/* <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" /> */}
                          <span className="text-base font-medium text-white">${sub?.planPrice  || '0.00'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${statusActive ? 'bg-green-900 text-white' : 'bg-red-900 text-white'}`}>
                          {statusActive ? (
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                          ) : (
                            <XCircleIcon className="h-3 w-3 mr-1" />
                          )}
                          {statusActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-base font-medium text-white">{sub.renewalDate ? new Date(sub.renewalDate).toLocaleDateString() : '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => viewDetail(sub)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primaryBlue text-white hover:bg-primaryBlue/90"
                        >
                          <EyeIcon className="h-4 w-4" />
                          View Detail
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-blueGray border-2 border-borderGray rounded-lg p-4">
          <div className="text-sm text-gray-400">
            Showing page {currentPage || 1} of {totalPages || 1} ({totalSubscriptions || 0} total)
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange((currentPage || 1) - 1)}
              disabled={(currentPage || 1) === 1}
              className="px-3 py-2 bg-blueGray text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Previous
            </button>

            {(() => {
              const pages = [];
              const maxVisiblePages = 5;
              const current = currentPage || 1;
              const total = totalPages || 1;
              let startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(total, startPage + maxVisiblePages - 1);
              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }
              if (startPage > 1) {
                pages.push(
                  <button key={1} onClick={() => handlePageChange(1)} className="px-3 py-2 bg-blueGray text-white rounded-lg hover:bg-gray-600 transition-colors">1</button>
                );
                if (startPage > 2) pages.push(<span key="e1" className="px-2 py-2 text-gray-400">...</span>);
              }
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-2 rounded-lg transition-colors ${current === i ? 'bg-primaryBlue text-white' : 'bg-blueGray text-white hover:bg-gray-600'}`}
                  >
                    {i}
                  </button>
                );
              }
              if (endPage < total) {
                if (endPage < total - 1) pages.push(<span key="e2" className="px-2 py-2 text-gray-400">...</span>);
                pages.push(
                  <button key={total} onClick={() => handlePageChange(total)} className="px-3 py-2 bg-blueGray text-white rounded-lg hover:bg-gray-600 transition-colors">{total}</button>
                );
              }
              return pages;
            })()}

            <button
              onClick={() => handlePageChange((currentPage || 1) + 1)}
              disabled={(currentPage || 1) === (totalPages || 1)}
              className="px-3 py-2 bg-blueGray text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}